import React, { useState, useEffect } from 'react';
import { Wrench, CheckCircle, Shield, Clock, Camera, Phone, MapPin, ChevronDown, ChevronUp, Users, Award, Truck, X, Calculator, ShoppingCart } from 'lucide-react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SEOHead from '../components/SEO/SEOHead';
import { useBrand } from '../components/BrandProvider';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const ServicePosePage = () => {
  const brand = useBrand();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [openFaq, setOpenFaq] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  
  const [storeType, setStoreType] = useState('monobloc_manuel');
  const [isEtage, setIsEtage] = useState(false);
  const [isHigher3m, setIsHigher3m] = useState(false);
  const [largeur, setLargeur] = useState('');
  const [pricingData, setPricingData] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/installation-pricing/`);
        setPricingData(response.data);
      } catch (err) {
        console.error('Erreur chargement prix:', err);
      }
    };
    fetchPricing();
  }, [BACKEND_URL]);

  const storeTypes = [
    { value: 'monobloc_manuel', label: 'Store monobloc ou traditionnel manuel' },
    { value: 'monobloc_electrique', label: 'Store monobloc électrique' },
    { value: 'semi_coffre_manuel', label: 'Store semi-coffre manuel' },
    { value: 'semi_coffre_electrique', label: 'Store semi-coffre électrique' },
    { value: 'coffre_manuel', label: 'Store coffre manuel' },
    { value: 'coffre_electrique', label: 'Store coffre électrique' },
    { value: 'double_pente_manuel', label: 'Store à double pente manuel' },
    { value: 'double_pente_electrique', label: 'Store à double pente électrique' },
    { value: 'lambrequin_seul', label: 'Lambrequin seul' },
    { value: 'lambrequin_deroulable_manuel', label: 'Lambrequin déroulable manuel' },
    { value: 'lambrequin_deroulable_electrique', label: 'Lambrequin déroulable électrique' },
  ];

  // Calculer le prix en utilisant useMemo pour éviter les re-renders
  const calculatedPrice = React.useMemo(() => {
    const priceData = pricingData.find(p => p.store_type === storeType);
    if (!priceData) return 0;
    const largeurNum = parseInt(largeur) || 0;
    const largeurM = largeurNum / 100;
    let basePrice = 0;
    if (largeurM <= 1.80) basePrice = priceData.price_0_180;
    else if (largeurM <= 3.80) basePrice = priceData.price_181_380;
    else if (largeurM <= 5.80) basePrice = priceData.price_381_580;
    else if (largeurM <= 7.80) basePrice = priceData.price_581_780;
    else if (largeurM <= 9.80) basePrice = priceData.price_781_980;
    else basePrice = priceData.price_981_1200;
    let finalPrice = basePrice;
    if (isEtage) finalPrice *= 1.2;
    if (isHigher3m) finalPrice *= 1.2;
    return Math.round(finalPrice * 100) / 100;
  }, [storeType, largeur, isEtage, isHigher3m, pricingData]);

  const handleAddToCart = () => {
    const selectedStoreType = storeTypes.find(s => s.value === storeType);
    const largeurNum = parseInt(largeur) || 0;
    const installationItem = {
      product: {
        id: `installation-${storeType}-${largeurNum}-${Date.now()}`,
        name: `Service de pose - ${selectedStoreType?.label || 'Store'}`,
        slug: 'service-pose',
        image: '/images/service-pose.jpg',
        supplier: 'Toile Store Banne',
        isInstallationService: true
      },
      configuration: {
        quantity: 1,
        storeType: storeType,
        storeTypeLabel: selectedStoreType?.label || '',
        largeur: largeurNum,
        isEtage: isEtage,
        isHigher3m: isHigher3m,
        description: `Installation ${selectedStoreType?.label || 'Store'} - Largeur: ${largeurNum}cm${isEtage ? ' - En étage' : ''}${isHigher3m ? ' - Hauteur > 3m' : ''}`
      },
      totalPrice: calculatedPrice
    };
    addToCart(installationItem);
    setAddedToCart(true);
    setTimeout(() => {
      setAddedToCart(false);
      setShowCalculator(false);
      navigate('/panier');
    }, 1500);
  };

  const prestationItems = [
    "Le démontage de l'ancienne toile (y compris le/les lambrequins)",
    "L'évacuation de l'ancienne toile en décharge",
    "Le nettoyage de l'armature du store",
    "La pose de la nouvelle toile",
    "Les réglages éventuels du store à l'identique avant pose",
    "Pour les stores électriques, le réglage des fins de courses"
  ];

  const garanties = [
    { icon: <Award className="w-8 h-8 text-brand" />, title: 'Garantie "Non Satisfait - Refait!"', description: "Si la prestation n'est pas conforme à votre commande, nous la refaisons. Garantie plafonnée à 3000€ par intervention." },
    { icon: <Shield className="w-8 h-8 text-brand" />, title: 'Assurance Responsabilité Civile', description: "Nos installateurs sont assurés. Couverture jusqu'à 10 000 000€ pour dommages corporels et 5 000 000€ pour dommages matériels." },
    { icon: <CheckCircle className="w-8 h-8 text-brand" />, title: 'Garantie "Dommages aux Biens"', description: "Vos biens personnels sont couverts en cas de dommage lors de l'intervention, dans la limite de 600€." }
  ];

  const etapes = [
    { numero: 1, titre: "Commandez votre toile", description: "Configurez et commandez votre toile sur mesure. Choisissez l'option « Toile + Pose » lors de votre achat." },
    { numero: 2, titre: "Nous vous contactons", description: "Dès réception de votre toile, nous vous appelons pour confirmer la date et le lieu du rendez-vous d'installation." },
    { numero: 3, titre: "Installation par un pro", description: "Un installateur qualifié intervient chez vous. Photo avant/après et document de réception émargé à l'issue." }
  ];

  const faqs = [
    { question: "Quand l'installation aura-t-elle lieu ?", answer: "Dès que vous recevez votre commande, nous confirmons ensemble le lieu et la date du rendez-vous de pose. Nous bloquons par avance un installateur pour la date prévisionnelle indiquée dans votre bon de commande." },
    { question: "Quelles zones sont couvertes par le service pose ?", answer: "Notre service d'installation est disponible en France métropolitaine et en Suisse." },
    { question: "Y a-t-il des majorations de prix ?", answer: "Une majoration de 20% s'applique au prix de la prestation dans les cas suivants : installation en étage ou installation à plus de 3 mètres du sol." },
    { question: "Que se passe-t-il si je ne suis pas satisfait ?", answer: "Notre garantie « Non Satisfait - Refait » vous permet de voir la prestation refaite si elle n'est pas conforme à ce que vous aviez commandé." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead title={`Service Pose - Installation de toile de store | ${brand.name}`} description="Confiez-nous l'installation de votre toile de store. Service professionnel, garantie satisfaction, intervention partout en France." url="/service-pose" />
      <Header />

      {showCalculator && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-brand text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calculator className="w-8 h-8" />
                  <h2 className="text-2xl font-bold">Votre devis immédiat</h2>
                </div>
                <button onClick={() => setShowCalculator(false)} className="p-2 hover:bg-white/20 rounded-full transition">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Votre store :</label>
                <select value={storeType} onChange={(e) => setStoreType(e.target.value)} className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-brand focus:outline-none transition">
                  {storeTypes.map((type) => (<option key={type.value} value={type.value}>{type.label}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-3">L'installation doit-elle se faire en étage ?</label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="etage" checked={isEtage === true} onChange={() => setIsEtage(true)} className="w-5 h-5 text-brand focus:ring-brand" />
                    <span className="text-gray-700">Oui</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="etage" checked={isEtage === false} onChange={() => setIsEtage(false)} className="w-5 h-5 text-brand focus:ring-brand" />
                    <span className="text-gray-700">Non</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-3">Le store est-il positionné à plus de 3 mètres du sol ?</label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="hauteur" checked={isHigher3m === true} onChange={() => setIsHigher3m(true)} className="w-5 h-5 text-brand focus:ring-brand" />
                    <span className="text-gray-700">Oui</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="hauteur" checked={isHigher3m === false} onChange={() => setIsHigher3m(false)} className="w-5 h-5 text-brand focus:ring-brand" />
                    <span className="text-gray-700">Non</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Largeur de la toile à installer :</label>
                <div className="flex items-center gap-2">
                  <input type="number" value={largeur} onChange={(e) => setLargeur(e.target.value)} placeholder="ex: 350" className="w-32 p-3 border-2 border-gray-200 rounded-lg focus:border-brand focus:outline-none transition text-center text-lg" />
                  <span className="text-gray-600 font-medium">cm</span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-100">
                <div className="text-center">
                  <p className="text-gray-600 mb-2">Prix estimé de l'installation :</p>
                  <p className="text-5xl font-bold text-brand">{calculatedPrice.toFixed(2)} €</p>
                  <p className="text-sm text-gray-500 mt-2">TTC</p>
                </div>
                {(isEtage || isHigher3m) && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                    <strong>Note :</strong> Une majoration de 20% est appliquée {isEtage && isHigher3m ? ' (étage + hauteur > 3m)' : isEtage ? ' (installation en étage)' : ' (hauteur > 3m)'}.
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-3">
                <button onClick={handleAddToCart} disabled={!largeur || addedToCart} className={`w-full py-4 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${addedToCart ? 'bg-green-500 text-white' : !largeur ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-brand text-white hover:opacity-90'}`}>
                  {addedToCart ? (<><CheckCircle className="w-5 h-5" />Ajouté au panier !</>) : (<><ShoppingCart className="w-5 h-5" />Ajouter au panier</>)}
                </button>
                {!largeur && (<p className="text-sm text-amber-600 text-center">Veuillez indiquer la largeur de votre toile</p>)}
                <button onClick={() => setShowCalculator(false)} className="w-full border-2 border-gray-200 text-gray-600 py-3 rounded-lg font-medium hover:bg-gray-50 transition">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="bg-gradient-to-br from-brand/5 to-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-brand/10 text-brand px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Wrench className="w-4 h-4" />Service professionnel
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Confiez-nous l'installation de votre toile de store</h1>
              <p className="text-lg text-gray-600 mb-8">C'est l'assurance d'un travail qui bénéficie d'une <strong>garantie de parfait achèvement</strong> + une <strong>garantie décennale de pose</strong>, et accompli <strong>dans les règles de l'art</strong> par un professionnel qualifié.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => setShowCalculator(true)} className="bg-brand text-white px-8 py-4 rounded-lg font-semibold hover:opacity-90 transition flex items-center justify-center gap-2">💰 Estimer le prix de ma pose</button>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gray-100 rounded-2xl p-8 shadow-lg">
                <img src="https://images.pexels.com/photos/9875408/pexels-photo-9875408.jpeg?w=600&h=400&fit=crop" alt="Installation professionnelle de store" className="rounded-xl w-full h-64 object-cover" />
                <div className="mt-6 flex items-center gap-4">
                  <div className="flex -space-x-2">
                    <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white font-bold">P</div>
                    <div className="w-10 h-10 bg-brand/80 rounded-full flex items-center justify-center text-white font-bold">R</div>
                    <div className="w-10 h-10 bg-brand/60 rounded-full flex items-center justify-center text-white font-bold">O</div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Installateurs qualifiés</p>
                    <p className="text-sm text-gray-500">Partout en France & Suisse</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-6 border-y">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3"><div className="bg-brand/10 p-3 rounded-full"><Users className="w-6 h-6 text-brand" /></div><div><p className="font-semibold text-gray-900">Pro qualifié</p><p className="text-sm text-gray-500">Installateur certifié</p></div></div>
            <div className="flex items-center gap-3"><div className="bg-brand/10 p-3 rounded-full"><Shield className="w-6 h-6 text-brand" /></div><div><p className="font-semibold text-gray-900">Garantie pose</p><p className="text-sm text-gray-500">Satisfait ou refait</p></div></div>
            <div className="flex items-center gap-3"><div className="bg-brand/10 p-3 rounded-full"><MapPin className="w-6 h-6 text-brand" /></div><div><p className="font-semibold text-gray-900">France & Suisse</p><p className="text-sm text-gray-500">Large couverture</p></div></div>
            <div className="flex items-center gap-3"><div className="bg-brand/10 p-3 rounded-full"><Camera className="w-6 h-6 text-brand" /></div><div><p className="font-semibold text-gray-900">Photo avant/après</p><p className="text-sm text-gray-500">Traçabilité totale</p></div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comment ça marche ?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Vous achetez librement sur notre site la <strong>toile seule</strong> ou bien la <strong>toile + pose</strong>. C'est vous qui décidez.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {etapes.map((etape, index) => (
              <div key={index} className="text-center">
                <div className="bg-brand/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                  <span className="absolute -top-2 -right-2 bg-brand text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg">{etape.numero}</span>
                  {etape.numero === 1 && <Truck className="w-10 h-10 text-brand" />}
                  {etape.numero === 2 && <Phone className="w-10 h-10 text-brand" />}
                  {etape.numero === 3 && <Wrench className="w-10 h-10 text-brand" />}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{etape.titre}</h3>
                <p className="text-gray-600">{etape.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Que comprend la prestation exactement ?</h2>
              <ul className="space-y-4">
                {prestationItems.map((item, index) => (<li key={index} className="flex items-start gap-3"><CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-gray-700">{item}</span></li>))}
              </ul>
              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm"><strong>Note :</strong> Pour éviter toute contestation ultérieure, une photo est prise avant l'intervention et une autre après. Un document de réception est émargé par le client à l'issue de l'intervention.</p>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Majorations éventuelles</h3>
              <p className="text-gray-600 mb-4">Une majoration de <strong className="text-brand">20%</strong> s'applique au prix de la prestation dans les cas suivants :</p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"><span className="text-2xl">🏢</span><span className="text-gray-700">Installation en étage</span></li>
                <li className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"><span className="text-2xl">📏</span><span className="text-gray-700">Installation à plus de 3 mètres du sol</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">L'installation est-elle garantie ?</h2>
            <p className="text-gray-600">Oui ! Voici nos 3 garanties pour votre tranquillité d'esprit.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {garanties.map((garantie, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-2xl hover:shadow-lg transition">
                <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow">{garantie.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{garantie.title}</h3>
                <p className="text-gray-600 text-sm">{garantie.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-12"><h2 className="text-3xl font-bold text-gray-900 mb-4">Questions fréquentes</h2></div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => toggleFaq(index)} className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  {openFaq === index ? <ChevronUp className="w-5 h-5 text-brand" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                {openFaq === index && (<div className="px-6 pb-6"><p className="text-gray-600">{faq.answer}</p></div>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Prêt à nous confier l'installation ?</h2>
          <p className="text-white/80 text-lg mb-8">Configurez votre toile et sélectionnez l'option « Pose » lors de votre commande.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => setShowCalculator(true)} className="bg-white text-brand px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition">💰 Estimer le prix de ma pose</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePosePage;
